// --- React
import { useCallback, useMemo, useState } from 'react';
// --- Config & Utils
import * as columnDefs from 'datastage/components/AGGrid/config/columns-def';
// --- Hooks
import { useDateMathData } from '../../../hooks/useDateMathData';

/**
 * Custom hook for Date Math grid utilities.
 *
 * Features:
 * - Manages sorting state and logic for the Date Math grid
 * - Fetches sorted date math jobs data from the API via useDateMathData
 * - Prepares column definitions with sorting controls
 *
 * @param {object} params - Optional configuration
 * @param {Array} params.columnHandlers - Handlers to pass to column definitions
 * @returns {object} { setSort, columns }
 */
export function useDateMathGridUtils({ columnHandlers = [] } = {}) {
    const { fetchDateMathJobs, updateDateMath, dateMathRange } = useDateMathData();
    const [sortedColId, setSortedColId] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    // Handle sorting: update state, fetch sorted data, update date math jobs
    const setSort = useCallback(
        async (colId, direction) => {
            setSortedColId(colId);
            setSortDirection(direction);

            const customSort = [colId, direction.toUpperCase()];
            const data = await fetchDateMathJobs(dateMathRange, null, customSort);

            if (data) {
                updateDateMath({
                    jobs: data?.content,
                    totalPages: data?.totalPages,
                    totalItems: data?.totalElements,
                    currentPage: data?.pageable?.pageNumber,
                    dateMathSort: customSort,
                });
            }
        },
        [fetchDateMathJobs, updateDateMath, dateMathRange],
    );

    // Prepare column definitions with sorting controls
    const baseColumns = columnDefs['dateMathJobs'](...columnHandlers)['default'];

    const columns = useMemo(
        () =>
            baseColumns.map((col) => ({
                ...col,
                headerComponentParams: {
                    ...col.headerComponentParams,
                    setSort,
                    sortedColId,
                    sortDirection,
                },
            })),
        [setSort, sortedColId, sortDirection, baseColumns],
    );

    return { setSort, columns };
}
